/** Clock-style units only. KPI unit `second` stays numeric so fractions keep precision. */
export type KpiDurationUnit = 'minute' | 'hour';

export interface KpiDurationParts {
    hours: number;
    minutes: number;
    seconds: number;
}

const secondsPerMinute = 60;
const secondsPerHour = 60 * 60;

export const isKpiDurationUnit = (
    units: string | null | undefined
): units is KpiDurationUnit => units === 'minute' || units === 'hour';

const unitToSeconds = (unit: KpiDurationUnit): number => {
    switch (unit) {
        case 'hour':
            return secondsPerHour;
        case 'minute':
            return secondsPerMinute;
    }
};

export const toKpiDurationSeconds = (
    value: number,
    unit: KpiDurationUnit
): number => Math.max(0, Math.floor(value * unitToSeconds(unit)));

export const fromKpiDurationSeconds = (
    totalSeconds: number,
    unit: KpiDurationUnit
): number => totalSeconds / unitToSeconds(unit);

export const kpiDurationPartsToSeconds = (parts: KpiDurationParts): number =>
    parts.hours * secondsPerHour +
    parts.minutes * secondsPerMinute +
    parts.seconds;

export const splitKpiDurationSeconds = (
    totalSeconds: number,
    unit: KpiDurationUnit
): KpiDurationParts => {
    if (unit === 'hour') {
        return {
            hours: Math.floor(totalSeconds / secondsPerHour),
            minutes: Math.floor(
                (totalSeconds % secondsPerHour) / secondsPerMinute
            ),
            seconds: totalSeconds % secondsPerMinute,
        };
    }

    return {
        hours: 0,
        minutes: Math.floor(totalSeconds / secondsPerMinute),
        seconds: totalSeconds % secondsPerMinute,
    };
};

export const formatKpiDurationSeconds = (
    totalSeconds: number,
    unit: KpiDurationUnit
): string => {
    // Keep clock arithmetic exact in both the browser and PDF formatter.
    if (
        !Number.isFinite(totalSeconds) ||
        totalSeconds > Number.MAX_SAFE_INTEGER
    ) {
        return '-';
    }

    const pad = (n: number): string => (n < 10 ? '0' : '') + n;
    const parts = splitKpiDurationSeconds(totalSeconds, unit);

    if (unit === 'hour') {
        return [parts.hours, parts.minutes, parts.seconds].map(pad).join(':');
    }

    return [parts.minutes, parts.seconds].map(pad).join(':');
};

export const formatKpiDurationValue = (
    value: number,
    unit: KpiDurationUnit
): string => formatKpiDurationSeconds(toKpiDurationSeconds(value, unit), unit);
